<template>
  <main>
    <LandingSectionWrapper
      v-for="(section,idx) in homepageSections"
      :key="idx"
      v-bind="section"
    >
      <LandingSection
        v-bind="section"
      >
        <ClaimUsernameForm v-if="section.hasSlot"/>
      </LandingSection>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#001239">
      <div class="large-cards-container">
        <LandingLargeCard
          v-for="(card,idx) in largeCardOptions"
          :key="idx"
          v-bind="card"
        />
      </div>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#F1F2ED">
      <LandingTitle
        title="Take your content business to the next level with our education dashboard"
        color="#333333"
        max-width="886px"
      />
      <div class="cards-container">
        <LandingCard
          v-for="(card,idx) in cardOptions"
          :key="idx"
          v-bind="card"
        />
      </div>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#570C59">
      <LandingTitle
        title="Choose a plan and start building your online presence"
        color="#fff"
        max-width="688px"
      />
      <div class="landing-button__container">
        <TLink
          class="landing-button analytics-go-to-pricing-page"
          style="background-color: #ffffff; color: #570c59"
          :to="{name:'Pricing'}"
        >
          Explore all plans
        </TLink>
      </div>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#DFBED8">
      <LandingTitle
        title="Join a community of creators, entrepreneurs, businesses and brands and start building your digital universe"
        color="#570C59"
        max-width="848px"
      />
      <CommunityCarousel :setup="carouselSetup"/>
      <div class="landing-button__container">
        <TLink
          class="landing-button analytics-go-to-signup"
          style="background-color: #570c59; color: #dfbed8"
          :to="{name:'SignUp'}"
        >
          Start your page
        </TLink>
      </div>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#F1F2ED">
      <Carousel
        class="carousel-container"
        :paginationEnabled="false"
        :navigation-enabled="true"
        :per-page="1"
        navigationNextLabel="<div class='navigation-button-next'/>"
        navigationPrevLabel="<div class='navigation-button-prev'/>"
      >
        <Slide
          class="slider-container"
          v-for="(item,idx) in carouselList"
          :key="idx"
        >
          <div class="slider-container__image">
            <img
              loading="lazy"
              alt="image"
              :src="item.imageSrc"
            >
          </div>
          <div class="slider-container__body">
            <div class="slider-container__quotes"/>
            <div class="slider-container__title">{{ item.title }}</div>
            <div class="slider-container__subtitle">{{ item.subtitle }}</div>
            <div class="slider-container__line"/>
          </div>
        </Slide>
      </Carousel>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#03153B">
      <LandingTitle
        title="Questions?"
        color="#fff"
      />
      <div class="faq-wrapper">
        <FAQBar
          v-for="(option,idx) in faqOptions"
          :key="idx"
          variant="dark"
        >
          <template v-slot:header>
            {{ option.title }}
          </template>
          <template v-slot:main>
            <div
              class="faq-wrapper__body-container"
              v-html="option.html"
            />
          </template>
        </FAQBar>
      </div>
    </LandingSectionWrapper>

    <LandingSectionWrapper background-color="#F1F2ED">
      <LandingTitle
        title="Choose a plan and start building your online presence"
        color="#03153B"
        max-width="688px"
      />
      <div class="landing-button__container">
        <TLink
          class="landing-button analytics-go-to-signup"
          style="background-color: #f4592f; color: #f1f2ed"
          :to="{name:'SignUp'}"
        >
          Start your page
        </TLink>
      </div>
    </LandingSectionWrapper>
  </main>
</template>

<script>
import LandingSectionWrapper from '@/components/landing/LandingSectionWrapper';
import LandingSection from '@/components/sections/LandingSection';
import ClaimUsernameForm from '@/components/forms/ClaimUsernameForm';
import LandingLargeCard from '@/components/cards/LandingLargeCard';
import LandingTitle from '@/components/landing/LandingTitle';
import LandingCard from '@/components/cards/LandingCard';
import CommunityCarousel from '@/pages/homepage/CommunityCarousel';
import FAQBar from '@/components/bars/FAQBar';
import TLink from '@/components/base/TLink/TLink';

// eslint-disable-next-line no-shadow
const Carousel = () => import('vue-carousel').then(({ Carousel }) => Carousel);
// eslint-disable-next-line no-shadow
const Slide = () => import('vue-carousel').then(({ Slide }) => Slide);

export default {
  name: 'TrybHomepage',
  components: {
    TLink,
    FAQBar,
    CommunityCarousel,
    LandingCard,
    LandingTitle,
    LandingLargeCard,
    ClaimUsernameForm,
    LandingSection,
    LandingSectionWrapper,
    Carousel,
    Slide,
  },
  data() {
    return {
      homepageSections: null,
      largeCardOptions: null,
      cardOptions: null,
      faqOptions: null,
      carouselList: null,
    };
  },
  computed: {
    carouselSetup() {
      return { category: '' };
    },
  },
  mounted() {
    this.homepageSections = [
      {
        title: 'The only link in bio page you’ll ever need.',
        description: 'Use Tryb to connect and build your entire online presence, on one link in bio page. Create, share and sell in minutes.',
        textColor: '#ffffff',
        backgroundColor: '#001239',
        image: {
          src: require('@/assets/images/homepage/link-bio.webp'),
        },
        isLargeTitle: true,
        buttonTextColor: '#001239',
        hasSlot: true,
        to: 'SignUp',
      },
      {
        title: 'Turn your followers into customers',
        description: 'Sell digital products and events, build online courses, add a virtual tip jar, and earn from paid subscriptions.',
        textColor: '#001239',
        backgroundColor: '#dad3ff',
        image: {
          src: require('@/assets/images/homepage/sell.webp'),
          isFirst: true,
        },
        buttonTextColor: '#dad3ff',
        to: 'SignUp',
      },
      {
        title: 'Take ownership of your audience',
        description: 'Direct followers to your page, drive offline traffic online with your QR code, build a mailing list and increase your sales.',
        textColor: '#ffffff',
        backgroundColor: '#570c59',
        image: {
          src: require('@/assets/images/homepage/own-your-audience.webp'),
        },
        buttonTextColor: '#570c59',
        to: 'SignUp',
      },
      {
        title: 'Showcase your personality & brand',
        description: 'Customise your Tryb page, create your own themes, add unlimited links, and embed music, videos and podcasts.',
        textColor: '#570c59',
        backgroundColor: '#dfbed8',
        image: {
          src: require('@/assets/images/homepage/customise.webp'),
          isFirst: true,
        },
        buttonTextColor: '#dfbed8',
        to: 'SignUp',
      },
    ];
    this.largeCardOptions = [
      {
        title: 'Analyse your page performance',
        description: 'Measure engagement, monitor revenue, track visitors, clicks and views, and better understand your audience with our detailed analytics.',
        imageSrc: require('@/assets/images/homepage/analytics.webp'),
      },
      {
        title: 'Connect with like-minded creators',
        description: 'Access an online community of creators and entrepreneurs. Learn from your peers, share ideas, and promote your brand.',
        imageSrc: require('@/assets/images/homepage/community.webp'),
      },
    ];
    this.cardOptions = [
      {
        imageSrc: require('@/assets/images/homepage/resources.webp'),
        description: 'Access exclusive creator resources',
      },
      {
        imageSrc: require('@/assets/images/homepage/creator-school.webp'),
        description: 'Attend the Tryb Creator Business School',
      },
      {
        imageSrc: require('@/assets/images/homepage/support.webp'),
        description: 'Receive support from industry experts',
      },
    ];
    this.faqOptions = [
      {
        title: 'What is a link in bio page?',
        html: '<p>Think of it as a mini-website. Or as we like to call it, a magic gateway to all of your content.</p>'
          + '<p>With a link in bio page you can consolidate your entire digital presence and direct followers wherever you like.</p>'
          + '<p>Once set up, you can share your page in your social media bios, email signature and newsletters. You can also add it to flyers, posters and packaging or anywhere else that your followers or potential customers will see it.</p>'
          + '<p>Used well, a link in bio page can become a traffic-driving powerhouse.</p>',
      },
      {
        title: 'Why do I need a link in bio page?',
        html: '<p>The more your brand grows, the more products, services and content you’ll want to promote (videos, podcast episodes, newsletters, blog posts, events, product listings, other social media pages etc).</p>'
          + '<p>The problem is, most social media platforms only allow you to add one link to your bio. With a link in bio page, you can bypass that rule.</p>',
      },
      {
        title: 'What makes Tryb better than other link in bio providers?',
        html: '<p><strong>Advanced customisation:</strong> With more ways to personalise your page than our competitors, you can make your page as unique as you are, and align it perfectly with your brand.</p>'
          + '<p><strong>Monetisation:</strong> Unlike other link in bio tools, a Tryb page will help you to monetise your expertise. Sell digital items, launch paid subscriptions, promote your services, or even collect tips. You can also use your page to attract sponsors and negotiate affiliate deals.</p>'
          + '<p><strong>Resources:</strong> Tryb members can access expert creator resources for free when they claim their page. And for those looking to take things to the next level, there’s even a Creator Business School.</p>'
          + '<p><strong>Community:</strong> As a Tryb member, you can also access the most valuable resource of all; each other</p>',
      },
      {
        title: 'Who is Tryb for?',
        html: '<p>Tryb is for anyone who has an online presence. Creators, entrepreneurs, businesses, brands, and students all use Tryb to curate their digital universe.</p>',
      },
      {
        title: 'Do I need a website to use Tryb?',
        html: '<p>No! In fact, many of our members choose to create a Tryb page as an alternative to a traditional website. Unlike a website, you can build a Tryb page in minutes, regardless of how tech-savvy you are. So you can start sharing, selling and growing in minutes. If you already have a website, that’s no problem, you can just add it to your Tryb page.</p>'
          + '<p>It’s also a far more affordable way to promote your content, and it offers analytics so that you can easily track the performance of your links.</p>',
      },
      {
        title: 'How many links should I have on my Tryb page?',
        html: '<p>We know that you probably have a lot that you want to share with your audience. That’s why we don’t put a limit on the number of links that you can add to your Tryb page.</p>'
          + '<p>Having said that, too many links can sometimes become overwhelming for your audience, so we generally recommend adding between 5 and 7 links.</p>',
      },
      {
        title: 'Can I make money from my Tryb page?',
        html: '<p>Absolutely. We offer a whole range of options to monetise your passion. Through your Tryb page you can sell digital items, online courses and virtual consultations. You can also launch paid subscriptions, promote events, direct followers to your product pages, and crowdfund for your next project. Plus, you can use your page to attract sponsors and negotiate affiliate deals.</p>',
      },
    ];
    this.carouselList = [
      {
        imageSrc: require('@/assets/images/homepage/testimonials/zoe.webp'),
        title: 'I love finding innovative ways to make life simpler for my customers. My Tryb page helps me to do just that.',
        subtitle: 'Zoe Chapman, Entrepreneur',
      },
      {
        imageSrc: require('@/assets/images/homepage/testimonials/kamna.webp'),
        title: 'I was looking for a link-in-bio tool that also lets me sell my digital products, without hefty fees. Tryb ticked all the boxes',
        subtitle: 'Kamna Kappor, Blogger',
      },
      {
        imageSrc: require('@/assets/images/homepage/testimonials/lusanda.webp'),
        title: 'What do I love about Tryb? It’s so easy to use, you can organise all of your links, and my fans can easily find what they need.',
        subtitle: 'Lusanda Beauchamp, Musician',
      },
      {
        imageSrc: require('@/assets/images/homepage/testimonials/inayah.webp'),
        title: 'I love using Tryb. It’s so easy to set up, and has been instrumental in helping me to monetise my passions.',
        subtitle: 'Inayah Sadiq, Travel content creator',
      },
      {
        imageSrc: require('@/assets/images/homepage/testimonials/adam.webp'),
        title: 'Tryb is an amazing platform. A lot of content creators in the streaming world don’t have a link in bio, so I plan to share with them how amazing Tryb is.',
        subtitle: 'Adam Fowler, Gamer',
      },
    ];
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/mixins";

.large-cards-container {
  display: flex;
  flex-direction: column;
  gap: 12px;

  @include media-lg() {
    display: flex;
    flex-direction: row;
    gap: 48px;
  }
}

.cards-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @include media-lg() {
    flex-direction: row;
    justify-content: space-between;
    gap: 0
  }
}

.faq-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__body-container {
    display: flex;
    flex-direction: column;
    gap: 16px;

    font-size: 16px;
    line-height: 24px;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.7);

    @include media-lg() {
      font-size: 18px;
    }

    & strong {
      color: var(--light);
      font-weight: 700;
    }
  }
}


.slider-container {
  display: flex;

  @include media-sm-only() {
    padding-bottom: 64px;
  }

  @include media-lg {
    align-items: center;
    flex-direction: row;
    gap: 110px;
  }

  &__image {
    display: none;

    @include media-lg() {
      display: block;
      flex: 0 1 50%;
      padding-left: 2px;
      height: 513px;
    }

    img {
      width: 100%;
      height: 100%;
    }
  }

  &__body {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;

    @include media-sm-only() {
      justify-content: center;
    }

    @include media-lg() {
      flex: 0 1 50%;
    }
  }

  &__quotes {
    margin: 0 auto 24px auto;

    width: 75px;
    height: 52px;
    background-image: url("~@/assets/images/homepage/quotes.png");
    background-size: contain;
    background-repeat: no-repeat;


    @include media-lg() {
      position: absolute;
      top: -48px;
      left: -48px;
      width: 186px;
      height: 136px;
      margin: 0;
    }
  }

  &__title {
    margin-bottom: 24px;
    flex-grow: 1;

    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    color: #03153b;
    text-align: center;

    @include media-lg() {
      margin-bottom: 32px;

      font-size: 40px;
      line-height: 48px;
      text-align: left;
    }
  }

  &__subtitle {
    margin-bottom: 24px;

    font-size: 16px;
    line-height: 24px;
    color: #03153b;
    text-align: center;

    @include media-lg() {
      margin-bottom: 32px;

      font-size: 20px;
      line-height: 32px;
      text-align: left;
    }
  }

  &__line {
    background-image: url("~@/assets/images/homepage/wave-line.svg");
    background-size: contain;
    background-repeat: no-repeat;
    height: 5.1px;

    @include media-sm-only() {
      margin-bottom: 24px;

      background-position: center;
    }
  }
}

.carousel-container {
  position: relative;

  .VueCarousel-navigation {
    position: absolute;
    bottom: 32px;
    right: 50%;

    @include media-lg() {
      bottom: 0;
      right: calc(50% - 90px);
    }

    .VueCarousel-navigation-button {
      &:focus {
        outline: none;
      }
    }
  }
}

.navigation-button-next {
  width: 48px;
  height: 48px;
  border: 1px solid #03153b;
  border-radius: 50%;
  background: url("~@/assets/icons/optimized/arrow_right.svg") no-repeat center;

  &:focus {
    outline: none;
  }
}

.navigation-button-prev {
  width: 48px;
  height: 48px;
  border: 1px solid #03153b;
  border-radius: 50%;
  background: url("~@/assets/icons/optimized/arrow_left.svg") no-repeat center;

  &:focus {
    outline: none;
  }
}

.landing-button {
  display: inline-block;
  text-align: center;
  padding: 12px 24px;
  border-radius: 100px;
  width: fit-content;

  @include media-sm-only() {
    width: 100%;
  }

  &__container {
    text-align: center;

    &_left {
      text-align: left;
    }
  }
}

</style>
